import React, {
  Children,
  cloneElement,
  FC,
  ReactElement,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
} from 'react';
import { EVENTS_NAME } from '@src/consts/common';
import { useEventEmitter } from '@src/hooks/useEventEmitter';
import { UnitContext } from './context';
import { IUnit, IUnitChildProps, IUnitEventDetails } from './models';
import { UnitEvent } from '@src/core/Unit/UnitEvent';
import { IComponentChildrenProps } from '@src/models/props';
import { AnyType } from '@src/models/common';

interface IProps extends IComponentChildrenProps, IUnit {}

export const Unit: FC<Omit<IProps, 'path'>> = (props) => {
  const { entityCtx, env, id } = props;

  const ref = useRef(null);
  const ctx = useContext(UnitContext);
  const emitter = useEventEmitter();
  const isShow = useRef<boolean>(false);

  const getUnitData = useCallback(
    () => ({
      entityCtx,
      id,
      env,
      path: ctx?.getUnitPath(),
    }),
    [ctx, entityCtx, env, id],
  );

  const unitData = useRef<IUnit>(getUnitData());

  const getUnitPath = useCallback(() => {
    const parentId = ctx?.getUnitPath();

    return parentId ? [unitData.current, ...parentId] : [];
  }, [ctx]);

  const registerEmitEvent = useCallback(
    (details: IUnitEventDetails) => {
      emitter.emit(EVENTS_NAME.UNIT_EVENT_LOG, {
        details,
        unit: unitData.current,
      });
    },
    [emitter],
  );

  useEffect(() => {
    unitData.current = getUnitData();

    /** Если обновились пропсы и документ уже помечен как "показанный", то отправляем обновленную статистику с новыми пропсами */
    if (isShow.current) {
      registerEmitEvent({ dataId: '$self', kind: 'view' });
    }
  }, [getUnitData, registerEmitEvent]);

  const children = useMemo(() => {
    if (Children.count(props.children) > 1) {
      throw new Error(`Unit id = ${id} имеет больше одного дочернего элемента!`);
    }

    if (typeof props.children === 'string') {
      throw new Error(`Unit id = ${id} дочерний элемент типа string! Дочерний элемент должен быть отличный от строки`);
    }

    return Children.map<ReactElement, AnyType>(props.children, (child) => {
      /** Переопределяем свойства потомка... */
      const newProps: IUnitChildProps = {
        /** ---------Накидываем нужные атрибуты */
        ['data-unit-id']: id,
        ['data-entity-id']: entityCtx?.entityId,
        ['data-object-id']: entityCtx?.objectId,
        /** --------- */

        /** --------- Обрабатываем обратные вызовы */
        onVisible: () => {
          if (!isShow.current) {
            registerEmitEvent({ dataId: '$self', kind: 'view' });
            child.props.onVisible?.();
            isShow.current = true;
          }
        },
        onClick: (e): void => {
          const target = e.target as HTMLDivElement;
          child.props.onClick?.(e);

          if (e.unit) {
            if (e.unit.isActiveEvent()) {
              registerEmitEvent(e.unit.getData().details);
              e.unit.done();
            }
          } else {
            /** Проверяем обработано ли событие уже каким то юнитом. Если событие не создано, вероятно это первая обработка */
            let isNotDone = false;

            if (!e.unit) {
              e.unit = new UnitEvent({
                details: {
                  dataId: '$self',
                  kind: 'click',
                },
              });
              isNotDone = true;
            }

            if (target && isNotDone) {
              const dataId = target.getAttribute('data-id');

              if (dataId) {
                registerEmitEvent({ dataId, kind: 'click' });
                e.unit.done();
              }
            }
          }
        },

        /** --------- */
        // Для корректной работы useIntersection необходимо определить ссылку,
        // либо копируем уже существующую, либо создаем свою в юните
        ref: child.ref || ref,
      };

      if (env && typeof env === 'object') {
        for (const k of Object.keys(env)) {
          // Без lowercase будем получать ошибку в консоль
          // "Warning: React does not recognize the `data-env-errorType` prop on a DOM element."
          newProps['data-env-' + k.toLowerCase()] = env[k];
        }
      }

      return cloneElement(child as AnyType, newProps);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [env, entityCtx?.entityId, entityCtx?.objectId, id, props.children, registerEmitEvent]);

  return <UnitContext.Provider value={{ getUnitPath }}>{children}</UnitContext.Provider>;
};
